'use strict';

var index = require('../xdr/index.js');
var keypair = require('./keypair.js');
var strkey = require('./strkey.js');
var claimPredicate = require('../xdr/generated/claim-predicate.js');
var claimantV0 = require('../xdr/generated/claimant-v0.js');
var claimant = require('../xdr/generated/claimant.js');

class Claimant {
  _destination;
  _predicate;
  /**
   * @param destination - The destination account ID.
   * @param predicate - The claim predicate.
   */
  constructor(destination, predicate) {
    if (!strkey.StrKey.isValidEd25519PublicKey(destination)) {
      throw new Error("Destination is invalid");
    }
    this._destination = destination;
    if (!predicate) {
      this._predicate = claimPredicate.ClaimPredicate.claimPredicateUnconditional();
    } else if (predicate instanceof claimPredicate.ClaimPredicate) {
      this._predicate = predicate;
    } else {
      throw new Error("Predicate should be an xdr.ClaimPredicate");
    }
  }
  /**
   * Returns an unconditional claim predicate
   */
  static predicateUnconditional() {
    return claimPredicate.ClaimPredicate.claimPredicateUnconditional();
  }
  /**
   * Returns an `and` claim predicate
   * @param left - an xdr.ClaimPredicate
   * @param right - an xdr.ClaimPredicate
   */
  static predicateAnd(left, right) {
    if (!(left instanceof claimPredicate.ClaimPredicate)) {
      throw new Error("left Predicate should be an xdr.ClaimPredicate");
    }
    if (!(right instanceof claimPredicate.ClaimPredicate)) {
      throw new Error("right Predicate should be an xdr.ClaimPredicate");
    }
    return claimPredicate.ClaimPredicate.claimPredicateAnd([left, right]);
  }
  /**
   * Returns an `or` claim predicate
   * @param left - an xdr.ClaimPredicate
   * @param right - an xdr.ClaimPredicate
   */
  static predicateOr(left, right) {
    if (!(left instanceof claimPredicate.ClaimPredicate)) {
      throw new Error("left Predicate should be an xdr.ClaimPredicate");
    }
    if (!(right instanceof claimPredicate.ClaimPredicate)) {
      throw new Error("right Predicate should be an xdr.ClaimPredicate");
    }
    return claimPredicate.ClaimPredicate.claimPredicateOr([left, right]);
  }
  /**
   * Returns a `not` claim predicate
   * @param predicate - an xdr.ClaimPredicate
   */
  static predicateNot(predicate) {
    if (!(predicate instanceof claimPredicate.ClaimPredicate)) {
      throw new Error("Predicate should be an xdr.ClaimPredicate");
    }
    return claimPredicate.ClaimPredicate.claimPredicateNot(predicate);
  }
  /**
   * Returns a `BeforeAbsoluteTime` claim predicate
   *
   * This predicate will be fulfilled if the closing time of the ledger that
   * includes the CreateClaimableBalance operation is less than this (absolute)
   * Unix timestamp (expressed in seconds).
   *
   * @param absBefore - Unix epoch (in seconds) as a string
   */
  static predicateBeforeAbsoluteTime(absBefore) {
    return claimPredicate.ClaimPredicate.claimPredicateBeforeAbsoluteTime(
      index.Int64.fromString(absBefore)
    );
  }
  /**
   * Returns a `BeforeRelativeTime` claim predicate
   *
   * This predicate will be fulfilled if the closing time of the ledger that
   * includes the CreateClaimableBalance operation plus this relative time delta
   * (in seconds) is less than the current time.
   *
   * @param seconds - seconds since closeTime of the ledger in which the ClaimableBalanceEntry was created (as string)
   */
  static predicateBeforeRelativeTime(seconds) {
    return claimPredicate.ClaimPredicate.claimPredicateBeforeRelativeTime(
      index.Int64.fromString(seconds)
    );
  }
  /**
   * Returns a claimant object from its XDR object representation.
   * @param claimantXdr - The claimant xdr object.
   */
  static fromXdr(claimantXdr) {
    let value;
    switch (claimantXdr.type) {
      case "claimantTypeV0":
        value = claimantXdr.value;
        return new this(
          strkey.StrKey.encodeEd25519PublicKey(value.destination.value.toBytes()),
          value.predicate
        );
      default:
        throw new Error(`Invalid claimant type: ${claimantXdr.type}`);
    }
  }
  /**
   * @deprecated Use {@link Claimant.fromXdr} instead.
   * Deprecated in version v17.0.0
   */
  static fromXDR(claimantXdr) {
    return Claimant.fromXdr(claimantXdr);
  }
  /**
   * Returns the xdr object for this claimant.
   */
  toXdrObject() {
    const claimant$1 = new claimantV0.ClaimantV0({
      destination: keypair.Keypair.fromPublicKey(this._destination).xdrAccountId(),
      predicate: this._predicate
    });
    return claimant.Claimant.claimantTypeV0(claimant$1);
  }
  /**
   * @deprecated Use {@link toXdrObject} instead.
   * Deprecated in version v17.0.0
   */
  toXDRObject() {
    return this.toXdrObject();
  }
  /**
   * The destination account ID.
   */
  get destination() {
    return this._destination;
  }
  set destination(_value) {
    throw new Error("Claimant is immutable");
  }
  /**
   * The claim predicate.
   */
  get predicate() {
    return this._predicate;
  }
  set predicate(_value) {
    throw new Error("Claimant is immutable");
  }
}

exports.Claimant = Claimant;
//# sourceMappingURL=claimant.js.map
